import { useCallback } from 'react'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import Styles from './ChatInput.module.scss'
import { ArrowUp, Stop } from 'blaze-ui/icons'
import { PaperClip } from 'almanac-ui/icons/12'
import { useThread } from 'hooks/queries/chat/useThread'
import { ToolsMenu } from './ToolsMenu'
import { RevertMenu } from './RevertMenu'
import { ChatActionsProps } from './Types'
import { useModals } from 'almanac-ui/Modal'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ACCEPTED_IMAGE_MIME_TYPES_NO_SVG } from 'screens/Finder/components/views/GalleryView/constants'
import { Button } from 'blaze-ui/Button/Button'
import type { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'

export const ChatActions = ({
  onChange,
  isDisabled,
  onSubmit,
  fileId,
  threadId,
  hasDesignContext,
  onAttachImage,
  isProcessing,
  onAbort,
}: ChatActionsProps) => {
  const { thread } = useThread({ id: threadId }).data || {}
  const { original_design_content, original_caption } = thread || {}
  const hasOriginalContent = original_design_content || original_caption
  const { openModal } = useModals()

  const handleAttach = useCallback(() => {
    openModal(
      ImageMenuModal,
      {
        title: 'Attach Image',
        accept: ACCEPTED_IMAGE_MIME_TYPES_NO_SVG,
        hiddenProviders: ['stockPhoto', 'stockVideo', 'gifs', 'blazeRecommendations', 'ai'],
        maxCount: 1,
        uploadMedia: true,
        showSearchBar: true,
        onUse: (images: ImageMenuSelectedImage[]) => {
          if (images.length > 0) {
            const image = images[0]
            onAttachImage?.({
              url: image.url,
              mediaFileKey: image.id || '',
            })
          }
        },
      },
      { newStack: true },
    )
  }, [openModal, onAttachImage])

  return (
    <div className={Styles.actions}>
      <div className={Styles.left}>
        {hasDesignContext ? (
          <Button
            aria-label="Attach"
            frontIcon={PaperClip}
            size="sm"
            variant="secondary"
            className={Styles.attachButton}
            onPress={handleAttach}
          >
            Attach
          </Button>
        ) : (
          <ToolsMenu onChange={onChange} />
        )}
        {hasOriginalContent && fileId && (
          <RevertMenu originalContent={original_design_content} originalCaption={original_caption} fileId={fileId} />
        )}
      </div>
      {isProcessing ? (
        <IconButton aria-label="Stop" icon={Stop} className={Styles.submitButton} onPress={onAbort} size="xs" rounded />
      ) : (
        <IconButton
          aria-label="Ask Blaze"
          icon={ArrowUp}
          className={Styles.submitButton}
          isDisabled={isDisabled}
          onPress={onSubmit}
          size="xs"
          rounded
        />
      )}
    </div>
  )
}
